import { z } from "zod";
/**
 * Contratos da central de atendimento.
 *
 * Ficam fora do `index.ts` por tamanho, não por arquitetura: é o mesmo pacote,
 * e a tela continua importando de `@rn/contracts`. O arquivo é autocontido de
 * propósito — importar do índice criaria ciclo, e ciclo em pacote de contrato é
 * o tipo de erro que só aparece no build de produção.
 */
// ── Presença do atendente ───────────────────────────────────────────────────
export const PRESENCE_STATUSES = ["online", "away", "busy", "offline"];
export const PRESENCE_LABEL = {
    online: "Disponível",
    away: "Ausente",
    busy: "Ocupado",
    offline: "Offline",
};
export const presenceUpdateSchema = z.object({
    status: z.enum(PRESENCE_STATUSES),
    statusMessage: z.string().trim().max(120).nullable().optional(),
    /** Teto individual de conversas simultâneas. 0 herda o teto da empresa. */
    maxConcurrent: z.number().int().min(0).max(200).optional(),
});
// ── Prioridade e fila ───────────────────────────────────────────────────────
export const CONVERSATION_PRIORITIES = ["low", "normal", "high", "urgent"];
export const CONVERSATION_PRIORITY_LABEL = {
    low: "Baixa",
    normal: "Normal",
    high: "Alta",
    urgent: "Urgente",
};
/**
 * Transferência de conversa.
 *
 * Aceita pessoa OU equipe: mandar para a equipe devolve para a fila e deixa
 * qualquer um pegar, que é o que se quer quando o atendente sai de turno.
 * `reason` não é enfeite — é o que aparece na linha do tempo para o próximo
 * entender por que a conversa caiu no colo dele.
 */
export const transferConversationSchema = z
    .object({
    assigneeId: z.string().uuid().nullable().optional(),
    teamId: z.string().uuid().nullable().optional(),
    reason: z.string().trim().max(280).optional(),
})
    .refine((v) => v.assigneeId !== undefined || v.teamId !== undefined, {
    message: "Escolha um atendente ou uma equipe",
});
export const CLOSE_REASONS = [
    "resolvido",
    "agendou",
    "sem_interesse",
    "fora_da_regiao",
    "so_preco",
    "nao_respondeu",
    "duplicado",
    "outro",
];
export const CLOSE_REASON_LABEL = {
    resolvido: "Resolvido",
    agendou: "Agendou avaliação",
    sem_interesse: "Sem interesse",
    fora_da_regiao: "Fora da região",
    so_preco: "Só queria preço",
    nao_respondeu: "Não respondeu",
    duplicado: "Duplicado",
    outro: "Outro",
};
export const closeConversationSchema = z.object({
    reason: z.enum(CLOSE_REASONS),
    note: z.string().trim().max(500).optional(),
    /** Dispara a pesquisa de satisfação, se estiver ligada nos ajustes. */
    askCsat: z.boolean().optional(),
});
// ── Respostas rápidas ───────────────────────────────────────────────────────
export const quickReplyCreateSchema = z.object({
    shortcut: z
        .string()
        .trim()
        .min(1)
        .max(40)
        // Sem espaço nem barra: o atalho é digitado depois de "/" na caixa de
        // texto, e espaço no meio faria o autocompletar parar no primeiro pedaço.
        .regex(/^[a-z0-9._-]+$/i, "Use letras, números, ponto, hífen ou underline"),
    title: z.string().trim().min(1).max(80),
    body: z.string().trim().min(1).max(4000),
    teamId: z.string().uuid().nullable().optional(),
    mediaUrl: z.string().url().max(500).nullable().optional(),
    mediaMimeType: z.string().trim().max(100).nullable().optional(),
    active: z.boolean().optional(),
});
export const quickReplyUpdateSchema = quickReplyCreateSchema
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: "Informe ao menos um campo" });
// ── Horário de atendimento ──────────────────────────────────────────────────
export const WEEKDAY_LABEL = {
    0: "Domingo",
    1: "Segunda",
    2: "Terça",
    3: "Quarta",
    4: "Quinta",
    5: "Sexta",
    6: "Sábado",
};
/** Faixa de atendimento em minutos desde a meia-noite: 8h = 480. */
export const businessHourSchema = z
    .object({
    weekday: z.number().int().min(0).max(6),
    startMinute: z.number().int().min(0).max(1439),
    endMinute: z.number().int().min(1).max(1440),
    teamId: z.string().uuid().nullable().optional(),
    active: z.boolean().optional(),
})
    .refine((v) => v.endMinute > v.startMinute, {
    message: "O fim precisa ser depois do início",
    path: ["endMinute"],
});
/** A tela manda a semana inteira de uma vez: substitui, não remenda. */
export const businessHoursReplaceSchema = z.object({
    teamId: z.string().uuid().nullable().optional(),
    hours: z.array(businessHourSchema).max(42),
});
// ── Ajustes da central ──────────────────────────────────────────────────────
export const attendanceSettingsSchema = z
    .object({
    timezone: z.string().trim().max(60).optional(),
    autoAssign: z.boolean().optional(),
    defaultTeamId: z.string().uuid().nullable().optional(),
    maxConcurrentPerAgent: z.number().int().min(0).max(200).optional(),
    greetingEnabled: z.boolean().optional(),
    greetingMessage: z.string().trim().max(1000).nullable().optional(),
    awayEnabled: z.boolean().optional(),
    awayMessage: z.string().trim().max(1000).nullable().optional(),
    awayCooldownMinutes: z.number().int().min(5).max(10080).optional(),
    autoCloseAfterMinutes: z.number().int().min(0).max(43200).optional(),
    csatEnabled: z.boolean().optional(),
    csatMessage: z.string().trim().max(1000).nullable().optional(),
    /** Acompanhamento de quem parou de responder. Uma mensagem, não uma régua. */
    followupEnabled: z.boolean().optional(),
    followupAfterMinutes: z.number().int().min(60).max(20160).optional(),
    followupMessage: z.string().trim().max(1000).nullable().optional(),
    followupNotifyAgent: z.boolean().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: "Informe ao menos um campo" });
// ── Relatório de atendimento ────────────────────────────────────────────────
export const attendanceReportQuerySchema = z.object({
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    teamId: z.string().uuid().optional(),
    /** Agrupamento do relatório. */
    groupBy: z.enum(["agent", "team", "hour", "weekday", "channel"]).default("agent"),
});
// ── Abrir conversa ──────────────────────────────────────────────────────────
/**
 * Começar uma conversa a partir do CRM.
 *
 * Aceita contato, lead ou número solto porque os três pontos de partida
 * existem na vida real: a ficha do lead, a ficha do contato e o telefone que
 * alguém ditou no balcão. Quem resolve o endereço é o servidor — deixar a tela
 * normalizar telefone é como o mesmo número vira duas conversas.
 */
export const openConversationSchema = z
    .object({
    contactId: z.string().uuid().optional(),
    leadId: z.string().uuid().optional(),
    address: z.string().trim().min(8).max(30).optional(),
    channel: z.enum(["whatsapp_cloud", "whatsapp_evolution"]).optional(),
    /**
     * Por qual número da empresa a conversa sai, quando há mais de um. Manda
     * no canal: o número já é de um provedor. Sem ele vale o número principal,
     * que é o comportamento de sempre.
     */
    integrationId: z.string().uuid().optional(),
})
    .refine((v) => Boolean(v.contactId ?? v.leadId ?? v.address), {
    message: "Informe o contato, o lead ou o número",
});
/**
 * Link que abre o WhatsApp no aplicativo ou no navegador.
 *
 * `wa.me` é o formato oficial e resolve sozinho para onde mandar: aplicativo no
 * celular, WhatsApp Web no computador. Mora no pacote de contratos porque a
 * mesma regra vale na ficha do lead, na do contato e na conversa — três cópias
 * é como uma delas esquece o 55.
 */
/**
 * Há quanto tempo, em palavras.
 *
 * Mora no contrato porque servidor e tela dizem a MESMA frase sobre a mesma
 * queda. Duas implementações divergiriam no primeiro ajuste — o aviso do sino
 * falando "há 2 horas" e a faixa da tela falando "há 1 hora" sobre o mesmo
 * WhatsApp é o tipo de contradição que faz a pessoa parar de confiar nos dois.
 */
export function descreverQueda(desde, agora) {
    const minutos = Math.max(0, Math.floor((agora.getTime() - desde.getTime()) / 60_000));
    if (minutos < 2)
        return "agora há pouco";
    if (minutos < 60)
        return `há ${minutos} minutos`;
    const horas = Math.floor(minutos / 60);
    if (horas < 24)
        return horas === 1 ? "há 1 hora" : `há ${horas} horas`;
    const dias = Math.floor(horas / 24);
    return dias === 1 ? "há 1 dia" : `há ${dias} dias`;
}
export function linkWhatsApp(phone, texto) {
    if (!phone)
        return null;
    const digitos = phone.replace(/\D/g, "");
    if (digitos.length < 10)
        return null;
    const comPais = digitos.startsWith("55") ? digitos : `55${digitos}`;
    const query = texto ? `?text=${encodeURIComponent(texto)}` : "";
    return `https://wa.me/${comPais}${query}`;
}
//# sourceMappingURL=attendance.js.map